import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Fade,
  Slide,
  Zoom,
  alpha,
  useTheme,
  styled,
  keyframes,
} from '@mui/material';
import {
  Science,
  Assignment,
  CheckCircle,
  Warning,
  AccessTime,
  Analytics,
  Visibility,
  Schedule,
  TrendingUp,
  Speed,
  Groups,
  Biotech,
  VerifiedUser,
  Engineering,
  Science as ChemicalEngineering,
  LocalShipping,
  Dashboard as DashboardIcon,
  Timeline,
  AutoAwesome,
  Close,
  Insights,
  Assignment as Certificate,
  Hub,
  ScatterPlot,
  Grain,
  Opacity,
  FilterVintage,
  Spa,
  // Professional Laboratory Icons
  Inventory2,
  PrecisionManufacturing,
  Psychology,
  Whatshot,
  TrendingUp as ICPIcon,
  Diamond,
  InvertColors,
  GppGood,
  MenuBook,
  TaskAlt,
  Build,
  LocalPharmacy,
  Flare,
  AutoGraph,
  Stars,
  WaterDrop,
  // Enhanced Professional Icons
  AccountTree,
  Timeline as TimelineIcon,
  Layers,
  CallSplit,
  DeviceHub,
  Radar,
  Schema,
  Polyline,
  TrendingUp as TrendingUpIcon,
  BarChart,
  ShowChart,
  MultilineChart,
  CandlestickChart,
  BubbleChart,
  PieChart,
  DonutSmall,
  DataUsage,
  QueryStats,
  MonitorHeart,
  NetworkCheck,
  Speed as SpeedIcon,
  FlashOn,
  Bolt,
  ElectricBolt,
  Celebration,
  EmojiEvents,
  WorkspacePremium,
  Verified,
  Security,
  Shield,
  Lock,
  VpnKey,
  Badge,
  MilitaryTech,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { SampleStatistics, Sample } from '../services/samplesService';

// Advanced animations for the network
const particleFlow = keyframes`
  0% { 
    transform: translateX(-50px) translateY(0px); 
    opacity: 0; 
    scale: 0.5;
  }
  50% { 
    opacity: 1; 
    scale: 1;
    filter: brightness(1.5);
  }
  100% { 
    transform: translateX(50px) translateY(0px); 
    opacity: 0; 
    scale: 0.5;
  }
`;

const networkPulse = keyframes`
  0% { 
    transform: scale(1) rotate(0deg); 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
  50% { 
    transform: scale(1.1) rotate(2deg); 
    box-shadow: 0 0 40px rgba(37, 99, 235, 0.6);
  }
  100% { 
    transform: scale(1) rotate(0deg); 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
`;

const constellationGlow = keyframes`
  0% { filter: brightness(1) saturate(1); }
  50% { filter: brightness(1.3) saturate(1.5); }
  100% { filter: brightness(1) saturate(1); }
`;

// Styled components for the network visualization
const SankeyContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '500px',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.primary.main, 0.02)} 50%, 
    ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  backdropFilter: 'blur(20px)',
}));

const InteractiveCard = styled(Box)<{ 
  active?: boolean; 
  nodeType?: 'reception' | 'preparation' | 'laboratory' | 'validation' | 'certificate' | 'complete';
  laboratory?: string;
  sampleCount?: number;
}>(({ theme, active, nodeType, laboratory, sampleCount = 0 }) => {
  const getNodeColor = () => {
    switch (nodeType) {
      case 'reception': return { main: '#3b82f6', dark: '#1d4ed8', accent: '#60a5fa' };
      case 'preparation': return { main: '#7c3aed', dark: '#5b21b6', accent: '#a78bfa' };
      case 'laboratory': 
        switch (laboratory) {
          case 'SAA': return { main: '#dc2626', dark: '#991b1b', accent: '#f87171' };
          case 'ICP': return { main: '#059669', dark: '#047857', accent: '#34d399' };
          case 'AU': return { main: '#d97706', dark: '#92400e', accent: '#fbbf24' };
          case 'VOLUMETRIE': return { main: '#7c2d12', dark: '#451a03', accent: '#a16207' };
          default: return { main: '#6b7280', dark: '#374151', accent: '#9ca3af' };
        }
      case 'validation': return { main: '#ea580c', dark: '#c2410c', accent: '#fb923c' };
      case 'certificate': return { main: '#0891b2', dark: '#0e7490', accent: '#22d3ee' };
      case 'complete': return { main: '#16a34a', dark: '#15803d', accent: '#4ade80' };
      default: return { main: '#6b7280', dark: '#374151', accent: '#9ca3af' };
    }
  };

  const colors = getNodeColor();
  const intensity = Math.min(sampleCount / 10, 1); // Normalize intensity based on sample count
  
  return {
    position: 'absolute',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '120px',
    height: '100px',
    borderRadius: '20px',
    background: `
      radial-gradient(circle at 30% 30%, ${alpha(colors.accent, 0.3)}, transparent 70%),
      linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 50%, ${colors.main} 100%)
    `,
    border: active 
      ? `2px solid ${theme.palette.warning.main}` 
      : `1px solid ${alpha(colors.accent, 0.4)}`,
    color: theme.palette.common.white,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: active ? 'scale(1.15) rotateY(5deg)' : 'scale(1)',
    zIndex: active ? 20 : sampleCount > 0 ? 10 : 5,
    boxShadow: active 
      ? `
        0 0 30px ${alpha(colors.main, 0.6)},
        0 10px 40px ${alpha(colors.main, 0.3)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}
      `
      : `
        0 4px 20px ${alpha(colors.main, intensity * 0.4)},
        0 2px 10px ${alpha(colors.main, 0.2)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
      `,
    backdropFilter: 'blur(20px)',
    overflow: 'hidden',
    '&:hover': {
      transform: 'scale(1.2) translateY(-8px) rotateY(10deg)',
      boxShadow: `
        0 0 40px ${alpha(colors.main, 0.8)},
        0 20px 60px ${alpha(colors.main, 0.4)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.3)}
      `,
      zIndex: 25,
      '& .pulse-ring': {
        animation: 'pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite',
      },
      '& .icon-container': {
        transform: 'scale(1.3) rotate(5deg)',
      },
      '& .progress-ring': {
        strokeDasharray: `${(sampleCount / 20) * 314} 314`,
      },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '20px',
      background: `conic-gradient(from 0deg, 
        ${alpha(colors.accent, 0.1)}, 
        ${alpha(colors.main, 0.2)}, 
        ${alpha(colors.accent, 0.1)})`,
      opacity: intensity,
      animation: active ? 'spin 3s linear infinite' : 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      borderRadius: '18px',
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.common.white, 0.1)} 0%, 
        transparent 50%, 
        ${alpha(colors.accent, 0.1)} 100%)`,
      pointerEvents: 'none',
    },
    '@keyframes pulse-ring': {
      '0%': {
        transform: 'scale(1)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(1.5)',
        opacity: 0,
      },
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  };
});

const SankeyFlow = styled('svg')<{ active?: boolean; thickness?: number }>(({ theme, active, thickness = 1 }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 2,
  '& .flow-path': {
    fill: 'none',
    stroke: active ? theme.palette.warning.main : alpha(theme.palette.primary.main, 0.4),
    strokeWidth: Math.max(2, thickness * 20),
    opacity: active ? 0.8 : 0.6,
    transition: 'all 0.4s ease',
    filter: active ? `drop-shadow(0 0 8px ${theme.palette.warning.main})` : 'none',
  },
  '& .flow-gradient': {
    stop: [
      { offset: '0%', stopColor: alpha(theme.palette.primary.main, 0.8) },
      { offset: '100%', stopColor: alpha(theme.palette.secondary.main, 0.4) },
    ],
  },
}));

const StatsCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.9)} 0%, 
    ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

interface NetworkNodeData {
  id: string;
  name: string;
  shortName: string;
  icon: React.ReactNode;
  x: number;
  y: number;
  type: 'reception' | 'preparation' | 'laboratory' | 'validation' | 'certificate' | 'complete';
  laboratory?: string;
  stage?: string;
  count: number;
  connections: string[];
  description: string;
}

// Using Sample interface from samplesService

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const sankeyRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<SampleStatistics | null>(null);
  const [selectedNode, setSelectedNode] = useState<NetworkNodeData | null>(null);
  const [stageSamples, setStageSamples] = useState<Sample[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [activeConnections, setActiveConnections] = useState<Set<string>>(new Set());
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [batchViewDialog, setBatchViewDialog] = useState(false);
  const [selectedBatchForView, setSelectedBatchForView] = useState<{
    batchNumber: string;
    samples: any[];
    laboratory: string;
  } | null>(null);

  // Define PERFECTLY SPACED Sankey flow layout - WIDE AND CLEAN!
  const networkNodes: NetworkNodeData[] = [
    // Column 1: Entry Point (X = 50)
    {
      id: 'reception',
      name: 'Réception',
      shortName: 'Réception\nÉchantillons',
      icon: <Inventory2 sx={{ fontSize: 16, color: '#fff' }} />,
      x: 50,
      y: 200,
      type: 'reception',
      count: statistics?.par_statut?.RECEPTION || 0,
      connections: ['prep_mecanique'],
      description: 'Accueil et enregistrement des échantillons',
    },

    // Column 2: Mechanical Prep (X = 220) - 170px spacing
    {
      id: 'prep_mecanique',
      name: 'Préparation Mécanique',
      shortName: 'Préparation\nMécanique',
      icon: <PrecisionManufacturing sx={{ fontSize: 16, color: '#fff' }} />,
      x: 220,
      y: 200,
      type: 'preparation',
      count: statistics?.par_statut?.PREPARATION_MECANIQUE || 0,
      connections: ['saa_prep', 'icp_prep', 'au_prep', 'vol_prep'],
      description: 'Broyage, tamisage et homogénéisation',
    },

    // Column 3: Chemical Preparation (X = 390) - 170px spacing, 110px vertical
    {
      id: 'saa_prep',
      name: 'SAA Préparation',
      shortName: 'Préparation\nSAA',
      icon: <Psychology sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 40,
      type: 'laboratory',
      laboratory: 'SAA',
      stage: 'PREP_CHIMIQUE',
      count: statistics?.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0,
      connections: ['saa_analyse'],
      description: 'Digestion SAA',
    },
    {
      id: 'icp_prep',
      name: 'ICP Préparation',
      shortName: 'Préparation\nICP',
      icon: <LocalPharmacy sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 150,
      type: 'laboratory',
      laboratory: 'ICP',
      stage: 'PREP_CHIMIQUE',
      count: statistics?.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
      connections: ['icp_analyse'],
      description: 'Digestion ICP',
    },
    {
      id: 'au_prep',
      name: 'AU Préparation',
      shortName: 'Préparation\nAU',
      icon: <Build sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 260,
      type: 'laboratory',
      laboratory: 'AU',
      stage: 'PREP_CHIMIQUE',
      count: statistics?.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
      connections: ['au_analyse'],
      description: 'Digestion AU',
    },
    {
      id: 'vol_prep',
      name: 'VOL Préparation',
      shortName: 'Préparation\nVolumétrie',
      icon: <InvertColors sx={{ fontSize: 16, color: '#fff' }} />,
      x: 390,
      y: 370,
      type: 'laboratory',
      laboratory: 'VOLUMETRIE',
      stage: 'PREP_CHIMIQUE',
      count: statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0,
      connections: ['vol_analyse'],
      description: 'Préparation Volumétrie',
    },

    // Column 4: Analysis (X = 560) - 170px spacing, 110px vertical
    {
      id: 'saa_analyse',
      name: 'SAA Analyse',
      shortName: 'Analyse\nSAA',
      icon: <Whatshot sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 40,
      type: 'laboratory',
      laboratory: 'SAA',
      stage: 'ANALYSE',
      count: statistics?.par_statut?.LABORATOIRE_SAA_ANALYSE || 0,
      connections: ['saa_validation'],
      description: 'Absorption atomique',
    },
    {
      id: 'icp_analyse',
      name: 'ICP Analyse',
      shortName: 'Analyse\nICP',
      icon: <AutoGraph sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 150,
      type: 'laboratory',
      laboratory: 'ICP',
      stage: 'ANALYSE',
      count: statistics?.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
      connections: ['icp_validation'],
      description: 'ICP-MS/OES',
    },
    {
      id: 'au_analyse',
      name: 'AU Analyse',
      shortName: 'Analyse\nAU',
      icon: <Diamond sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 260,
      type: 'laboratory',
      laboratory: 'AU',
      stage: 'ANALYSE',
      count: statistics?.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
      connections: ['au_validation'],
      description: 'Coupellation',
    },
    {
      id: 'vol_analyse',
      name: 'VOL Analyse',
      shortName: 'Analyse\nVolumétrie',
      icon: <WaterDrop sx={{ fontSize: 16, color: '#fff' }} />,
      x: 560,
      y: 370,
      type: 'laboratory',
      laboratory: 'VOLUMETRIE',
      stage: 'ANALYSE',
      count: statistics?.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0,
      connections: ['vol_validation'],
      description: 'Titrage',
    },

    // Column 5: Validation (X = 730) - 170px spacing, 110px vertical
    {
      id: 'saa_validation',
      name: 'SAA Validation',
      shortName: 'Validation\nSAA',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 40,
      type: 'validation',
      laboratory: 'SAA',
      count: statistics?.par_statut?.VALIDATION_SERVICE || 0,
      connections: ['certificate'],
      description: 'Validation SAA',
    },
    {
      id: 'icp_validation',
      name: 'ICP Validation',
      shortName: 'Validation\nICP',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 150,
      type: 'validation',
      laboratory: 'ICP',
      count: 0,
      connections: ['certificate'],
      description: 'Validation ICP',
    },
    {
      id: 'au_validation',
      name: 'AU Validation',
      shortName: 'Validation\nAU',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 260,
      type: 'validation',
      laboratory: 'AU',
      count: 0,
      connections: ['certificate'],
      description: 'Validation AU',
    },
    {
      id: 'vol_validation',
      name: 'VOL Validation',
      shortName: 'Validation\nVolumétrie',
      icon: <GppGood sx={{ fontSize: 16, color: '#fff' }} />,
      x: 730,
      y: 370,
      type: 'validation',
      laboratory: 'VOLUMETRIE',
      count: 0,
      connections: ['certificate'],
      description: 'Validation VOL',
    },

    // Column 6: Final Stages (X = 900) - 170px spacing, properly separated
    {
      id: 'certificate',
      name: 'Certificat',
      shortName: 'Génération\nCertificat',
      icon: <MenuBook sx={{ fontSize: 16, color: '#fff' }} />,
      x: 900,
      y: 160,
      type: 'certificate',
      count: statistics?.par_statut?.GENERATION_CERTIFICAT || 0,
      connections: ['complete'],
      description: 'Génération certificats',
    },
    {
      id: 'complete',
      name: 'Terminé',
      shortName: 'Échantillons\nTerminés',
      icon: <TaskAlt sx={{ fontSize: 16, color: '#fff' }} />,
      x: 900,
      y: 270,
      type: 'complete',
      count: statistics?.par_statut?.PRET || 0,
      connections: [],
      description: 'Échantillons prêts',
    },
  ];

  useEffect(() => {
    loadDashboardData();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Animate active connections based on sample flow
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate active connections based on sample counts
      const activeNodes = networkNodes.filter(node => node.count > 0);
      const newActiveConnections = new Set<string>();
      
      activeNodes.forEach(node => {
        node.connections.forEach(connId => {
          newActiveConnections.add(`${node.id}-${connId}`);
        });
      });
      
      setActiveConnections(newActiveConnections);
    }, 2000);

    return () => clearInterval(interval);
  }, [statistics]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const stats = await samplesService.getStatistics();
      setStatistics(stats);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Erreur lors du chargement des données du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = async (node: NetworkNodeData) => {
    setSelectedNode(node);
    setLoadingSamples(true);
    setDialogOpen(true);
    
    try {
      // Map node info to correct SampleStatus enum values
      const statusFilters: string[] = [];
      
      if (node.id === 'reception') {
        statusFilters.push('RECEPTION');
      } else if (node.id === 'prep_mecanique') {
        statusFilters.push('PREPARATION_MECANIQUE');
      } else if (node.type === 'validation') {
        statusFilters.push('VALIDATION_SERVICE');
      } else if (node.laboratory && node.stage) {
        statusFilters.push(`LABORATOIRE_${node.laboratory}_${node.stage}`);
      } else if (node.id === 'certificate') {
        statusFilters.push('GENERATION_CERTIFICAT');
      } else if (node.id === 'complete') {
        statusFilters.push('PRET');
      }

      if (statusFilters.length > 0) {
        // Use the proper samplesService with status filters
        const response = await samplesService.getSamples(1, 50, {
          status: statusFilters as any[]
        });
        
        let filteredSamples = response.samples || [];
        
        // Additional filtering for laboratory-specific validation nodes
        if (node.type === 'validation' && node.laboratory) {
          filteredSamples = filteredSamples.filter(sample => {
            const sampleData = sample as any; // Cast to any to access extended properties
            if (node.laboratory === 'SAA') {
              return sampleData.current_laboratory === 'LABORATOIRE_SAA' ||
                     sample.metadonnees?.saa_analysis_form ||
                     sample.metadonnees?.saa_analysis_completed_at;
            } else if (node.laboratory === 'ICP') {
              return sampleData.current_laboratory === 'LABORATOIRE_ICP' ||
                     sample.metadonnees?.icp_analysis_form ||
                     sample.metadonnees?.icp_analysis_completed_at;
            } else if (node.laboratory === 'AU') {
              return sampleData.current_laboratory === 'LABORATOIRE_AU' ||
                     sample.metadonnees?.au_analysis_form ||
                     sample.metadonnees?.au_analysis_completed_at;
            } else if (node.laboratory === 'VOLUMETRIE') {
              return sampleData.current_laboratory === 'LABORATOIRE_VOLUMETRIE' ||
                     sample.metadonnees?.volumetrie_analysis_form ||
                     sample.metadonnees?.volumetrie_analysis_completed_at;
            }
            return true;
          });
        }
        

        setStageSamples(filteredSamples);
      } else {
        setStageSamples([]);
      }
    } catch (error) {
      console.error('Error loading node samples:', error);
      setStageSamples([]);
    } finally {
      setLoadingSamples(false);
    }
  };

  const renderSankeyFlows = () => {
    return networkNodes.map(node => 
      node.connections.map(targetId => {
        const target = networkNodes.find(n => n.id === targetId);
        if (!target) return null;

        const connectionId = `${node.id}-${targetId}`;
        const isActive = activeConnections.has(connectionId);
        const flowThickness = Math.max(0.05, Math.min(node.count / 15, 1)); // Scale thickness based on sample count

        // Create curved path for Sankey flow
        const startX = node.x + 50;
        const startY = node.y + 30;
        const endX = target.x + 50;
        const endY = target.y + 30;
        
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        const controlX1 = startX + (endX - startX) * 0.3;
        const controlY1 = startY;
        const controlX2 = endX - (endX - startX) * 0.3;
        const controlY2 = endY;

        const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;

        return (
          <SankeyFlow key={connectionId} active={isActive} thickness={flowThickness}>
            <defs>
              <linearGradient id={`gradient-${connectionId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={alpha(theme.palette.primary.main, 0.8)} />
                <stop offset="30%" stopColor={alpha(theme.palette.secondary.main, 0.9)} />
                <stop offset="70%" stopColor={alpha(theme.palette.success.main, 0.8)} />
                <stop offset="100%" stopColor={alpha(theme.palette.info.main, 0.6)} />
              </linearGradient>
              {isActive && (
                <filter id={`glow-${connectionId}`}>
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              )}
            </defs>
            <path
              className="flow-path"
              d={pathData}
              stroke={`url(#gradient-${connectionId})`}
              filter={isActive ? `url(#glow-${connectionId})` : 'none'}
              strokeDasharray={isActive ? '10 5' : 'none'}
            />
          </SankeyFlow>
        );
      })
    );
  };

  const getActiveNode = () => {
    return networkNodes.reduce((max, node) => 
      node.count > max.count ? node : max, 
      networkNodes[0]
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'default';
    }
  };

  const getStatusDisplay = (statut: string) => {
    switch (statut) {
      case 'RECEPTION': return { text: 'Réception', color: theme.palette.info.main, bgColor: theme.palette.info.main };
      case 'PREPARATION_MECANIQUE': return { text: 'Préparation Mécanique', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_SAA_PREP_CHIMIQUE': return { text: 'Préparation SAA', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_SAA_ANALYSE': return { text: 'Analyse SAA', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'LABORATOIRE_ICP_PREP_CHIMIQUE': return { text: 'Préparation ICP', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_ICP_ANALYSE': return { text: 'Analyse ICP', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'LABORATOIRE_AU_PREP_CHIMIQUE': return { text: 'Préparation AU', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_AU_ANALYSE': return { text: 'Analyse AU', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE': return { text: 'Préparation VOL', color: theme.palette.warning.main, bgColor: theme.palette.warning.main };
      case 'LABORATOIRE_VOLUMETRIE_ANALYSE': return { text: 'Analyse VOL', color: theme.palette.primary.main, bgColor: theme.palette.primary.main };
      case 'VALIDATION_SERVICE': return { text: 'Validation', color: theme.palette.secondary.main, bgColor: theme.palette.secondary.main };
      case 'GENERATION_CERTIFICAT': return { text: 'Génération Certificat', color: theme.palette.success.main, bgColor: theme.palette.success.main };
      case 'PRET': return { text: 'Terminé', color: theme.palette.success.main, bgColor: theme.palette.success.main };
      case 'REJETE': return { text: 'Rejeté', color: theme.palette.error.main, bgColor: theme.palette.error.main };
      default: return { text: statut, color: theme.palette.text.secondary, bgColor: theme.palette.grey[500] };
    }
  };

  // Helper functions for batch/lot visualization
  const groupSamplesByBatch = (samples: any[]) => {
    const batches: Record<string, any[]> = {};
    const individualSamples: any[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: any): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  const getBatchNumber = (sample: any): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchNumber)) {
      newExpanded.delete(batchNumber);
    } else {
      newExpanded.add(batchNumber);
    }
    setExpandedBatches(newExpanded);
  };

  const getTotalSamples = () => statistics?.total || 0;

  const handleViewBatch = (batchNumber: string, samples: any[], laboratory: string) => {
    setSelectedBatchForView({ batchNumber, samples, laboratory });
    setBatchViewDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '70vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Initialisation du réseau de laboratoire...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Futuristic Header */}
      <Box sx={{ 
        mb: 4, 
        p: 4,
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.1)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.1)} 50%, 
          ${alpha(theme.palette.success.main, 0.1)} 100%)`,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Ccircle cx="2" cy="2" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          zIndex: 0,
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative', zIndex: 1 }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main', 
            width: 64, 
            height: 64,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            animation: `${networkPulse} 3s ease-in-out infinite`
          }}>
            <Hub sx={{ fontSize: 32 }} />
          </Avatar>
    <Box>
            <Typography variant="h3" component="h1" fontWeight={800} sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              🧬 AFRILAB Network Control
        </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScatterPlot sx={{ fontSize: 20 }} />
              Réseau Interactif des Laboratoires - {user?.firstName} {user?.lastName}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ position: 'relative', zIndex: 1 }}>
          🔬 Visualisation en temps réel du flux d'échantillons à travers tous les laboratoires
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Fade in={!!error}>
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
        </Fade>
      )}

      {/* ENHANCED ADMIN CONTROL CENTER */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Row 1: Core Metrics */}
        <Grid item xs={12} md={3}>
          <Zoom in style={{ transitionDelay: '100ms' }}>
            <StatsCard sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'primary.main', 
                    width: 56, 
                    height: 56,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                  }}>
                    <Assignment sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" fontWeight={800} color="primary.main">
                      {getTotalSamples()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      +12% vs hier
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                    Total Échantillons
                  </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tous laboratoires confondus
                  </Typography>
              </CardContent>
            </StatsCard>
          </Zoom>
        </Grid>

        <Grid item xs={12} md={3}>
          <Zoom in style={{ transitionDelay: '200ms' }}>
            <StatsCard sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'warning.main', 
                    width: 56, 
                    height: 56,
                    background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                  }}>
                    <Timeline sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" fontWeight={800} color="warning.main">
                      {Object.values(statistics?.par_statut || {})
                        .filter((_, index, arr) => index < arr.length - 1)
                        .reduce((sum, count) => sum + count, 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Flux actif
                  </Typography>
                </Box>
              </Box>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  En Traitement
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Dans le pipeline
                </Typography>
            </CardContent>
            </StatsCard>
          </Zoom>
        </Grid>

        <Grid item xs={12} md={3}>
          <Zoom in style={{ transitionDelay: '300ms' }}>
            <StatsCard sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'success.main', 
                    width: 56, 
                    height: 56,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                  }}>
                    <CheckCircle sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" fontWeight={800} color="success.main">
                      {statistics?.par_statut?.PRET || 0}
                  </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      +8% vs hier
                  </Typography>
                </Box>
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  Terminés
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Prêts à livrer
                </Typography>
              </CardContent>
            </StatsCard>
          </Zoom>
        </Grid>

        <Grid item xs={12} md={3}>
          <Zoom in style={{ transitionDelay: '400ms' }}>
            <StatsCard sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'info.main', 
                    width: 56, 
                    height: 56,
                    background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`
                  }}>
                    <Speed sx={{ fontSize: 28 }} />
                </Avatar>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" fontWeight={800} color="info.main">
                      {statistics?.moyenne_duree_jours || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      -1.2 vs mois dernier
                    </Typography>
              </Box>
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  Jours Moyens
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Temps de traitement
                </Typography>
            </CardContent>
            </StatsCard>
          </Zoom>
        </Grid>

        {/* Row 2: Performance Indicators */}
        <Grid item xs={12} md={4}>
          <Zoom in style={{ transitionDelay: '500ms' }}>
            <StatsCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 48, height: 48 }}>
                    <Warning sx={{ fontSize: 24 }} />
                  </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={700} color="error.main">
                      {(statistics?.par_statut?.GENERATION_CERTIFICAT || 0) > 5 ? 'ALERT' : 'OK'}
                  </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Goulot d'étranglement
                  </Typography>
                </Box>
              </Box>
                <Typography variant="body2" color="text.secondary">
                  {(statistics?.par_statut?.GENERATION_CERTIFICAT || 0) > 5 
                    ? 'Certificats en attente' 
                    : 'Flux normal'
                  }
                </Typography>
            </CardContent>
            </StatsCard>
          </Zoom>
        </Grid>

        <Grid item xs={12} md={4}>
          <Zoom in style={{ transitionDelay: '600ms' }}>
            <StatsCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                    <Groups sx={{ fontSize: 24 }} />
                  </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={700} color="secondary.main">
                      94%
                  </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Efficacité système
                  </Typography>
                </Box>
              </Box>
                <Typography variant="body2" color="text.secondary">
                  Performance globale
                </Typography>
            </CardContent>
            </StatsCard>
          </Zoom>
      </Grid>

        <Grid item xs={12} md={4}>
          <Zoom in style={{ transitionDelay: '700ms' }}>
            <StatsCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    <Analytics sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {Math.round(((statistics?.par_statut?.PRET || 0) / (getTotalSamples() || 1)) * 100)}%
                      </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Taux de completion
                      </Typography>
                    </Box>
                  </Box>
                <Typography variant="body2" color="text.secondary">
                  Échantillons finalisés
                </Typography>
                </CardContent>
            </StatsCard>
          </Zoom>
            </Grid>
        </Grid>

      {/* The Spectacular Network Visualization */}
      <Slide direction="up" in mountOnEnter>
        <Card sx={{ mb: 4, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Timeline sx={{ fontSize: 28, color: 'primary.main' }} />
                Diagramme de Flux Sankey - Laboratoires AFRILAB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualisation du flux d'échantillons • L'épaisseur des connexions représente le volume des échantillons
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Inventory2 sx={{ fontSize: 12 }} /> Réception  
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Whatshot sx={{ fontSize: 12 }} /> SAA (Flamme)
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AutoGraph sx={{ fontSize: 12 }} /> ICP (Spectro)
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Diamond sx={{ fontSize: 12 }} /> AU (Or)
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WaterDrop sx={{ fontSize: 12 }} /> Volumétrie
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <GppGood sx={{ fontSize: 12 }} /> Validation
                </Typography>
              </Box>
            </Box>
            
            <SankeyContainer ref={sankeyRef}>
              {renderSankeyFlows()}
              
              {networkNodes.map((node) => (
                <InteractiveCard
                  key={node.id}
                  active={node.count > 0}
                  nodeType={node.type}
                  laboratory={node.laboratory}
                  sampleCount={node.count}
                  onClick={() => handleNodeClick(node)}
                  sx={{
                    left: node.x,
                    top: node.y,
                  }}
                >
                  {/* Pulse Ring Effect */}
                  <Box 
                    className="pulse-ring"
                    sx={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      right: '10px',
                      bottom: '10px',
                      borderRadius: '15px',
                      border: '2px solid currentColor',
                      opacity: node.count > 0 ? 0.6 : 0,
                      pointerEvents: 'none',
                    }}
                  />
                  
                  {/* Progress Ring - Smaller and repositioned */}
                  <Box sx={{ position: 'absolute', top: '4px', right: '4px' }}>
                    <svg width="16" height="16" style={{ transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        fill="none"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="1.5"
                      />
                      <circle
                        className="progress-ring"
                        cx="8"
                        cy="8"
                        r="6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeDasharray={`${Math.min((node.count / 20) * 38, 38)} 38`}
                        strokeLinecap="round"
                        style={{
                          transition: 'stroke-dasharray 0.3s ease',
                          opacity: 0.8,
                        }}
                      />
                    </svg>
                  </Box>

                  {/* Icon Container - Top */}
                  <Box 
                    className="icon-container"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      transition: 'transform 0.3s ease',
                      mb: 1,
                      mt: 0.5,
                    }}
                  >
                    {node.icon}
                  </Box>
                  
                  {/* PROMINENT Sample Count - TOP CENTER */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 10,
                      backgroundColor: alpha(theme.palette.common.white, 0.25),
                      borderRadius: '12px',
                      padding: '4px 10px',
                      minWidth: '40px',
                      textAlign: 'center',
                      mb: 1,
                      border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      fontWeight={900} 
                      sx={{ 
                        fontSize: '1.6rem',
                        color: theme.palette.common.white,
                        textShadow: '0 2px 6px rgba(0,0,0,0.7)',
                        lineHeight: 1,
                        filter: 'brightness(1.3)',
                      }}
                    >
                      {node.count}
                </Typography>
                  </Box>
                  
                  {/* Descriptive Name - Multi-line, BOTTOM */}
                  <Typography 
                    variant="caption" 
                    fontWeight={700} 
                    sx={{ 
                      fontSize: '0.65rem',
                      opacity: 0.95,
                      textAlign: 'center',
                      whiteSpace: 'pre-line',
                      lineHeight: 1.1,
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {node.shortName}
                  </Typography>
                  


                  {/* Activity Indicator */}
                  {node.count > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '6px',
                        left: '6px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.warning.main,
                        animation: 'pulse 2s infinite',
                        boxShadow: `0 0 8px ${theme.palette.warning.main}`,
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.7, transform: 'scale(1.2)' },
                        },
                      }}
                    />
                  )}
                </InteractiveCard>
              ))}
              
              {/* Flow direction indicators */}
              <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  padding: 1,
                  borderRadius: 1
                }}>
                  <Timeline sx={{ fontSize: 16 }} />
                  Flux des échantillons • Épaisseur = Volume
                  </Typography>
                </Box>
            </SankeyContainer>
          </CardContent>
        </Card>
      </Slide>

      {/* SPECTACULAR ENTERPRISE-GRADE DIALOG */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            minHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: selectedNode?.laboratory 
            ? `linear-gradient(135deg, 
                ${selectedNode.type === 'laboratory' && selectedNode.laboratory === 'SAA' ? '#dc2626' : 
                  selectedNode.type === 'laboratory' && selectedNode.laboratory === 'ICP' ? '#059669' : 
                  selectedNode.type === 'laboratory' && selectedNode.laboratory === 'AU' ? '#d97706' : 
                  selectedNode.type === 'laboratory' && selectedNode.laboratory === 'VOLUMETRIE' ? '#7c2d12' : 
                  theme.palette.primary.main}, 
                ${theme.palette.secondary.main})`
            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="2" cy="2" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            zIndex: 0,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
            <Avatar sx={{ 
              width: 64, 
              height: 64, 
              bgcolor: alpha(theme.palette.common.white, 0.2),
              border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`
            }}>
              {selectedNode?.icon}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                {selectedNode?.name}
              </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {selectedNode?.description}
                  </Typography>
                <Chip 
                  label={`${selectedNode?.count} échantillons`}
                  sx={{ 
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                {selectedNode?.laboratory && (
                  <Chip 
                    label={selectedNode.laboratory}
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15),
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
                </Box>
              </Box>
          </Box>
          <IconButton 
            onClick={() => setDialogOpen(false)} 
            sx={{ 
              color: 'white',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.1)
              }
            }}
          >
            <Close sx={{ fontSize: 28 }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {loadingSamples ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 8,
              flexDirection: 'column',
              gap: 2 
            }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="h6" color="text.secondary">
                Analyse des échantillons en cours...
              </Typography>
            </Box>
          ) : stageSamples.length > 0 ? (
            <Box>
              {/* ENHANCED BATCH/LOT VISUALIZATION */}
              {(() => {
                // Group samples by batch using proper batch logic (same as other dashboards)
                const { batches, individualSamples } = groupSamplesByBatch(stageSamples);
                const hasBatches = Object.keys(batches).length > 0;

                return (
                  <>
                    {/* SUMMARY STATISTICS */}
                    <Box sx={{ 
                      p: 3, 
                      background: `linear-gradient(135deg, 
                        ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                        ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <Schema sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {stageSamples.length}
                              </Typography>
                              <Typography variant="caption">
                                Total Échantillons
                              </Typography>
            </CardContent>
          </Card>
        </Grid>

                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <AccountTree sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {Object.keys(batches).length}
                </Typography>
                              <Typography variant="caption">
                                Lots Détectés
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <MonitorHeart sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {individualSamples.length}
                              </Typography>
                              <Typography variant="caption">
                                Échantillons Individuels
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Card sx={{ 
                            background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                            color: 'white'
                          }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                              <NetworkCheck sx={{ fontSize: 32, mb: 1 }} />
                              <Typography variant="h6" fontWeight={700}>
                                {new Set(stageSamples.map(s => s.client?.company_name)).size}
                              </Typography>
                              <Typography variant="caption">
                                Clients Distincts
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
              </Box>

                    {/* BATCH/LOT TABLE VISUALIZATION */}
                    <Box sx={{ 
                      maxHeight: '60vh', 
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: alpha(theme.palette.grey[300], 0.3),
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: alpha(theme.palette.primary.main, 0.4),
                        borderRadius: '10px',
                      },
                    }}>
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <TableCell sx={{ fontWeight: 700 }}>Échantillon / Lot</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Priorité</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Render Batch Samples First */}
                            {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                              const firstSample = batchSamples[0];
                              const isExpanded = expandedBatches.has(batchNumber);
                              const statusDisplay = getStatusDisplay(firstSample.statut);

                              return (
                                <React.Fragment key={batchNumber}>
                                  {/* Batch Header Row - Clickable */}
                                  <TableRow 
                                    sx={{ 
                                      backgroundColor: '#e3f2fd',
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: '#bbdefb' },
                                      borderLeft: '4px solid #2196f3'
                                    }}
                                    onClick={() => toggleBatchExpansion(batchNumber)}
                                  >
                                    <TableCell colSpan={6}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <IconButton size="small" sx={{ p: 0 }}>
                                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                        <Box sx={{ 
                                          backgroundColor: '#2196f3', 
                                          color: 'white', 
                                          px: 2, 
                                          py: 0.5, 
                                          borderRadius: 1,
                                          fontWeight: 'bold'
                                        }}>
                                          LOT
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                          {batchNumber}
                            </Typography>
                              <Chip 
                                          label={`${batchSamples.length} échantillons`} 
                                size="small" 
                                          color="primary" 
                                          variant="outlined"
                                        />
                                        <Chip 
                                          label={statusDisplay.text} 
                                          size="small" 
                                          sx={{ 
                                            backgroundColor: alpha(statusDisplay.bgColor as string, 0.1),
                                            color: statusDisplay.color,
                                            fontWeight: 500
                                          }}
                                          variant="outlined"
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                          Client: {firstSample.client?.company_name || 'N/A'}
                                        </Typography>
              <Button 
                size="small" 
                                          variant="contained"
                                          color="primary"
                startIcon={<Visibility />}
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent batch expansion when clicking button
                                            handleViewBatch(batchNumber, batchSamples, firstSample.laboratory || '');
                                          }}
                                          sx={{ ml: 'auto' }}
              >
                                          Voir le Lot
              </Button>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  
                                  {/* Individual Sample Rows - Only show when expanded */}
                                  {isExpanded && batchSamples.map((sample) => {
                                    const sampleStatus = getStatusDisplay(sample.statut);
                                    return (
                                      <TableRow key={sample.id} sx={{ backgroundColor: '#f8f9ff' }}>
                                        <TableCell sx={{ pl: 6 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                              <Science sx={{ fontSize: 16 }} />
                                            </Avatar>
                                            <Box>
                                              <Typography variant="body2" fontWeight={600} color="primary.main">
                                                {sample.sample_id}
              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                ID: {sample.id.slice(0, 8)}...
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={500}>
                                            {sample.external_id || 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={sample.type_echantillon} 
                                            size="small" 
                                            sx={{ fontWeight: 500 }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={sample.priorite} 
                                            size="small" 
                                            color={getPriorityColor(sample.priorite)}
                                            sx={{ fontWeight: 500 }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ 
                                              width: 8, 
                                              height: 8, 
                                              borderRadius: '50%', 
                                              bgcolor: sampleStatus.bgColor 
                                            }} />
                                            <Typography variant="caption" sx={{ color: sampleStatus.color }} fontWeight={600}>
                                              {sampleStatus.text}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                                              size="small" 
                                              startIcon={<Visibility />}
                                              onClick={() => navigate(`/samples/${sample.id}`)}
                                              sx={{ minWidth: 'auto' }}
                                            >
                                              Voir
                    </Button>
                  <Button
                                              size="small"
                                              startIcon={<QueryStats />}
                                              sx={{ minWidth: 'auto' }}
                                            >
                                              Stats
                  </Button>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}

                            {/* Render Individual Samples */}
                            {individualSamples.map((sample) => {
                              const statusDisplay = getStatusDisplay(sample.statut);
                              return (
                                <TableRow key={sample.id} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                        <Science sx={{ fontSize: 16 }} />
                                      </Avatar>
                                      <Box>
                                        <Typography variant="body2" fontWeight={600} color="primary.main">
                                          {sample.sample_id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ID: {sample.id.slice(0, 8)}...
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>
                                        {sample.client?.company_name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Réf: {sample.external_id || 'Non spécifiée'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.type_echantillon} 
                                      size="small" 
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.priorite} 
                                      size="small" 
                                      color={getPriorityColor(sample.priorite)}
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        bgcolor: statusDisplay.bgColor 
                                      }} />
                                      <Typography variant="caption" sx={{ color: statusDisplay.color }} fontWeight={600}>
                                        {statusDisplay.text}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                                        size="small" 
                                        startIcon={<Visibility />}
                                        onClick={() => navigate(`/samples/${sample.id}`)}
                                        sx={{ minWidth: 'auto' }}
                                      >
                                        Voir
                    </Button>
                                      <Button
                                        size="small"
                                        startIcon={<QueryStats />}
                                        sx={{ minWidth: 'auto' }}
                                      >
                                        Stats
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </>
                );
              })()}
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              background: `radial-gradient(circle, 
                ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                transparent 70%)`
            }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mx: 'auto',
                mb: 2
              }}>
                <Schema sx={{ fontSize: 40, color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                Station Vide
          </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Aucun échantillon présent dans cette station du réseau
          </Typography>
              <Typography variant="body2" color="text.secondary">
                Les échantillons apparaîtront ici lorsqu'ils atteindront cette étape du processus
              </Typography>
            </Box>
      )}
        </DialogContent>
      </Dialog>

      {/* Batch View Dialog */}
      <Dialog 
        open={batchViewDialog} 
        onClose={() => setBatchViewDialog(false)}
        maxWidth={false}
                      fullWidth
        PaperProps={{ 
          sx: { 
            minHeight: '80vh',
            maxWidth: '95vw',
            backgroundColor: '#f8f9fa'
          } 
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Visibility sx={{ fontSize: '1.8rem' }} />
              <Box>
                <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  📦 Vue Détaillée du Lot: {selectedBatchForView?.batchNumber}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                  {selectedBatchForView?.samples.length} échantillons • Laboratoire: {selectedBatchForView?.laboratory}
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => setBatchViewDialog(false)}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedBatchForView && (
            <Box>
              {/* Batch Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Nombre d'Échantillons</Typography>
                      <Typography variant="h4" color="primary">
                        {selectedBatchForView.samples.length}
                      </Typography>
                    </CardContent>
                  </Card>
                  </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Laboratoire Assigné</Typography>
                      <Typography variant="h6" color="info.main">
                        {selectedBatchForView.laboratory}
                      </Typography>
                    </CardContent>
                  </Card>
              </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Clients Distincts</Typography>
                      <Typography variant="h4" color="success.main">
                        {new Set(selectedBatchForView.samples.map(s => s.client?.company_name)).size}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Statuts</Typography>
                      <Typography variant="h6" color="warning.main">
                        {new Set(selectedBatchForView.samples.map(s => s.statut)).size} différents
                      </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

              {/* Detailed Sample Table */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Détails des Échantillons du Lot
          </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Position</TableCell>
                          <TableCell>ID Échantillon</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Date Réception</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBatchForView.samples.map((sample, index) => {
                          const batchPosition = sample.metadonnees?.batch_info?.bag_position || index + 1;
                          const statusDisplay = getStatusDisplay(sample.statut);
                          return (
                            <TableRow key={sample.id}>
                              <TableCell>
                                <Chip 
                                  label={`Sac ${batchPosition}/${selectedBatchForView.samples.length}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {sample.sample_id}
          </Typography>
                              </TableCell>
                              <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                              <TableCell>{sample.type_echantillon}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={sample.priorite} 
                                  size="small" 
                                  color={getPriorityColor(sample.priorite)}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={statusDisplay.text}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: alpha(statusDisplay.color, 0.1),
                                    color: statusDisplay.color,
                                    border: `1px solid ${alpha(statusDisplay.color, 0.3)}`
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(sample.date_reception).toLocaleDateString('fr-FR')}
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={() => {
                                    setBatchViewDialog(false);
                                    navigate(`/samples/${sample.id}`);
                                  }}
                                >
                                  Voir Détail
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Dashboard; 